import type {
  ActivityMessage,
  DocumentReferencePreview,
  ResponseContext,
  UserDocument,
} from "@/api/models"
import type { QueryClient } from "@tanstack/react-query"

import { useCanvasStore } from "@/features/canvas/stores/canvas-store"
import { logger } from "@/lib/logger"
import { queryKeys } from "@/lib/query-keys"
import { DocumentService } from "@/services/document-service"
import { SettingsService } from "@/services/settings-service"
import { useContentSidebarStore } from "@/stores/content-sidebar-store"

export type DocumentUpdateMetadata = {
  changeSummary?: string
  previousVersion?: number | null
  updatedVersion?: number | null
}

export function getActivityDocumentReferences(
  activityMsg: ActivityMessage
): DocumentReferencePreview[] {
  const singleIncomingDocumentReference = normalizeDocumentReference(
    activityMsg.details?.document_reference
  )
  const listIncomingDocumentReferences = normalizeDocumentReferences(
    activityMsg.details?.document_references
  )
  return [
    ...(singleIncomingDocumentReference ? [singleIncomingDocumentReference] : []),
    ...listIncomingDocumentReferences,
  ]
}

export function getActivityUpdateMetadata(
  activityMsg: ActivityMessage
): DocumentUpdateMetadata | undefined {
  const rawUpdateMetadata =
    activityMsg.details?.update_metadata && typeof activityMsg.details.update_metadata === "object"
      ? (activityMsg.details.update_metadata as Record<string, unknown>)
      : null
  if (!rawUpdateMetadata) {
    return undefined
  }

  return {
    changeSummary:
      typeof rawUpdateMetadata.change_summary === "string"
        ? rawUpdateMetadata.change_summary
        : undefined,
    previousVersion:
      typeof rawUpdateMetadata.previous_version === "number"
        ? rawUpdateMetadata.previous_version
        : null,
    updatedVersion:
      typeof rawUpdateMetadata.updated_version === "number"
        ? rawUpdateMetadata.updated_version
        : null,
  }
}

export function isDocumentCompletionActivity(activityType: string): boolean {
  return (
    activityType === "doc_creator_completed" ||
    activityType === "doc_updater_completed" ||
    activityType === "doc_content_reader_completed" ||
    activityType === "doc_internal_search_completed"
  )
}

export function normalizeDocumentReference(raw: unknown): DocumentReferencePreview | null {
  if (!raw || typeof raw !== "object") {
    return null
  }

  const candidate = raw as Partial<DocumentReferencePreview>
  if (
    typeof candidate.document_id !== "string" ||
    typeof candidate.title !== "string" ||
    typeof candidate.snippet !== "string"
  ) {
    return null
  }

  return {
    document_id: candidate.document_id,
    title: candidate.title,
    snippet: candidate.snippet,
    change_type: candidate.change_type,
    version: candidate.version,
    updated_at: candidate.updated_at,
  }
}

export function normalizeDocumentReferences(raw: unknown): DocumentReferencePreview[] {
  if (!Array.isArray(raw)) {
    return []
  }

  return raw
    .map((item) => normalizeDocumentReference(item))
    .filter((item): item is DocumentReferencePreview => item !== null)
}

export function mergeDocumentReferences(
  current: DocumentReferencePreview[] | undefined,
  incoming: DocumentReferencePreview[]
): DocumentReferencePreview[] {
  if (incoming.length === 0) {
    return current ?? []
  }

  const merged = [...(current ?? []), ...incoming]
  const deduped = new Map<string, DocumentReferencePreview>()

  merged.forEach((reference) => {
    const key = `${reference.document_id}:${reference.version ?? "latest"}:${reference.change_type ?? "updated"}`
    deduped.set(key, reference)
  })

  return Array.from(deduped.values())
}

type MergeIncomingActivityOptions = {
  existingActivities: ActivityMessage[]
  activityMsg: ActivityMessage
  activityTranslationKey: unknown
  activityKey: string
}

export function mergeIncomingActivity({
  existingActivities,
  activityMsg,
  activityTranslationKey,
  activityKey,
}: MergeIncomingActivityOptions): ActivityMessage[] {
  if (activityTranslationKey === "reasoning_summary_live") {
    const liveIndex = existingActivities.findLastIndex(
      (act) => act.details?.translation_key === "reasoning_summary_live"
    )
    return liveIndex >= 0
      ? existingActivities.map((act, index) => (index === liveIndex ? activityMsg : act))
      : [...existingActivities, activityMsg]
  }

  const exists = existingActivities.some(
    (act) => `${act.timestamp}_${act.activity_type}` === activityKey
  )
  if (exists) {
    return existingActivities
  }

  const nextActivities =
    activityTranslationKey === "reasoning_summary"
      ? existingActivities.filter(
          (act) => act.details?.translation_key !== "reasoning_summary_live"
        )
      : existingActivities
  return [...nextActivities, activityMsg]
}

/**
 * Merge persisted `response.context` from `message_saved` into the in-memory/streaming
 * context. Unions `document_references` (deduped; later entries win on the same key) so
 * save-only previews from the tool loop are not dropped when streaming already produced
 * refs. Prefer streaming `tool_approvals` when present; other keys (e.g. `documents`) come
 * from the save payload spread.
 *
 * Future simplification: if the backend guarantees a single authoritative final `context`
 * (e.g. full `documents` emitted before `response_complete`, or one canonical payload on
 * `message_saved` that always supersedes stream state), the extra merge paths in the
 * stream handler and WebSocket handler can shrink—today they bridge races between chunks,
 * `response_complete`, persistence, and finalize settle.
 */
export function mergeSavedResponseContext(
  streamContext: ResponseContext | undefined,
  savedContext: ResponseContext | undefined
): ResponseContext {
  if (!savedContext) {
    return (
      streamContext ?? {
        data_points: [],
        followup_questions: null,
        thoughts: [],
      }
    )
  }

  const base = streamContext ?? {
    data_points: [],
    followup_questions: null,
    thoughts: [],
  }

  const normalizedSavedRefs = normalizeDocumentReferences(savedContext.document_references)
  const mergedRefs = mergeDocumentReferences(base.document_references, normalizedSavedRefs)

  return {
    ...base,
    ...savedContext,
    document_references: mergedRefs,
    tool_approvals: base.tool_approvals ?? savedContext.tool_approvals,
  }
}

function mapDocumentVersionsToCanvas(versions: UserDocument["versions"] | undefined) {
  return (versions ?? []).map((version) => ({
    version: version.version,
    content: version.content,
    timestamp: version.timestamp,
    is_ai_edit: version.edit_source === "ai",
    ai_edit_preview: version.edit_preview,
  }))
}

export async function syncActiveCanvasDocument(
  reference: DocumentReferencePreview,
  updateMetadata?: DocumentUpdateMetadata
): Promise<void> {
  const canvasState = useCanvasStore.getState()
  const sidebarState = useContentSidebarStore.getState()
  const isCanvasOpen = sidebarState.isOpen && sidebarState.currentPanel?.id === "canvas"
  if (!isCanvasOpen || !canvasState.documentId) {
    return
  }
  if (canvasState.documentId !== reference.document_id) {
    return
  }

  const latestCanvasVersion = canvasState.versions[canvasState.versions.length - 1]?.version ?? 0
  const stagedVersion = canvasState.pendingRemoteUpdate?.nextVersion ?? 0
  if (
    typeof reference.version === "number" &&
    Math.max(latestCanvasVersion, stagedVersion) >= reference.version
  ) {
    return
  }

  const idToken = SettingsService.getInstance().token
  if (!idToken) {
    return
  }

  try {
    const document = await DocumentService.getDocument(reference.document_id, idToken)
    useCanvasStore.setState((currentState) => {
      const currentSidebarState = useContentSidebarStore.getState()
      const isCanvasPanelOpen =
        currentSidebarState.isOpen && currentSidebarState.currentPanel?.id === "canvas"
      if (!isCanvasPanelOpen || currentState.documentId !== reference.document_id) {
        return currentState
      }

      const nextVersions = mapDocumentVersionsToCanvas(document.versions)
      const latestFetchedVersion =
        nextVersions.length > 0 ? nextVersions[nextVersions.length - 1].version : null
      const nextVersion =
        updateMetadata?.updatedVersion ?? latestFetchedVersion ?? reference.version ?? null
      const pendingVersion = currentState.pendingRemoteUpdate?.nextVersion ?? null
      if (
        typeof nextVersion === "number" &&
        typeof pendingVersion === "number" &&
        pendingVersion >= nextVersion
      ) {
        return currentState
      }

      const changeSummaryRaw = updateMetadata?.changeSummary
      const latestCurrentVersion =
        currentState.versions.length > 0
          ? currentState.versions[currentState.versions.length - 1].version
          : null
      const previousVersion = updateMetadata?.previousVersion ?? latestCurrentVersion
      // Defensive fallback for unexpected runtime payloads.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      const nextContent = document.content ?? ""
      if (
        currentState.content === nextContent &&
        typeof nextVersion === "number" &&
        previousVersion === nextVersion
      ) {
        return currentState
      }

      return {
        pendingRemoteUpdate: {
          documentId: reference.document_id,
          changeSummary: changeSummaryRaw ?? reference.snippet,
          previousContent: currentState.content,
          previousVersion,
          nextContent,
          nextVersion: typeof nextVersion === "number" ? nextVersion : null,
          nextTitle: document.title,
          nextVersions,
          nextEtag:
            typeof (document as { _etag?: unknown })._etag === "string"
              ? ((document as { _etag?: string })._etag ?? null)
              : currentState.etag,
          nextUpdatedAt: document.updated ?? currentState.lastSavedAt,
        },
      }
    })
  } catch (error) {
    logger.warn("Failed to sync active canvas document after tool update", {
      documentId: reference.document_id,
      error: error instanceof Error ? error.message : String(error),
    })
  }
}

/** Stable identity for an activity, matching the websocket handler's dedupe key. */
export function activitySideEffectKey(activity: ActivityMessage): string {
  return `${activity.timestamp}_${activity.activity_type}`
}

/**
 * Side effects an activity has on data outside the chat transcript: refresh
 * the open canvas when a tool rewrote its document, and invalidate the list
 * queries a tool's creation affects.
 *
 * Single entry point for BOTH transports (#2961): the legacy websocket
 * handler ran these inline, while durable turns copied `snapshot.activities`
 * straight into state and never ran them — so a chat-driven document
 * expansion left the open editor stale until it was reopened from the card.
 * Idempotent per activity: the canvas sync checks versions, invalidations
 * are cheap, so replaying a snapshot on re-attach is harmless.
 */
export function applyActivitySideEffects(
  activityMsg: ActivityMessage,
  queryClient: QueryClient
): void {
  const documentReferences = getActivityDocumentReferences(activityMsg)
  if (documentReferences.length > 0 && isDocumentCompletionActivity(activityMsg.activity_type)) {
    const updateMetadata = getActivityUpdateMetadata(activityMsg)
    documentReferences.forEach((reference) => {
      void syncActiveCanvasDocument(reference, updateMetadata)
    })
  }

  switch (activityMsg.activity_type) {
    case "doc_creator_completed":
      void queryClient.invalidateQueries({ queryKey: queryKeys.documents() })
      break
    case "doc_updater_completed":
      // The documents list shows "updated" timestamps and the cached single
      // document must not serve the pre-edit content on the next open.
      void queryClient.invalidateQueries({ queryKey: queryKeys.documents() })
      documentReferences.forEach((reference) => {
        void queryClient.invalidateQueries({ queryKey: queryKeys.document(reference.document_id) })
        void queryClient.invalidateQueries({
          queryKey: queryKeys.documentVersions(reference.document_id),
        })
      })
      break
    case "saved_prompt_created":
      void queryClient.invalidateQueries({ queryKey: queryKeys.prompts })
      break
    case "project_created":
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects() })
      break
    case "memory_suggested":
      void queryClient.invalidateQueries({ queryKey: ["memories"] })
      break
    default:
      break
  }
}
